import * as Utils from "./Utils";
var resolveMetaUpdate = function (meta, update) {
    if (update === undefined) {
        return meta;
    }
    if (typeof update === "function") {
        return update(meta);
    }
    return update;
};
export var DEFAULT_STATE = {
    defaultValue: undefined,
    error: undefined,
    pending: false,
    pendingAt: null,
    rejected: false,
    rejectedAt: null,
    resolved: false,
    resolvedAt: null,
    settled: false,
    settledAt: null,
    submitType: undefined,
    value: undefined,
    cancelled: false,
    cancelledAt: null,
    meta: null,
};
var AsyncState = /** @class */ (function () {
    function AsyncState() {
        this.defaultValue = DEFAULT_STATE.defaultValue;
        this.value = DEFAULT_STATE.value;
        this.error = DEFAULT_STATE.error;
        this.pending = DEFAULT_STATE.pending;
        this.pendingAt = DEFAULT_STATE.pendingAt;
        this.rejected = DEFAULT_STATE.rejected;
        this.rejectedAt = DEFAULT_STATE.rejectedAt;
        this.resolved = DEFAULT_STATE.resolved;
        this.resolvedAt = DEFAULT_STATE.resolvedAt;
        this.settled = DEFAULT_STATE.settled;
        this.settledAt = DEFAULT_STATE.settledAt;
        this.submitType = DEFAULT_STATE.submitType;
        this.cancelled = DEFAULT_STATE.cancelled;
        this.cancelledAt = DEFAULT_STATE.cancelledAt;
        this.meta = DEFAULT_STATE.meta;
        // prevents new AsyncState()
    }
    AsyncState.create = function (defaultValue, options) {
        if (options === void 0) { options = {}; }
        return Utils.assign({}, DEFAULT_STATE, {
            meta: options.meta || null,
            defaultValue: defaultValue,
            value: defaultValue !== null && defaultValue !== void 0 ? defaultValue : undefined,
            pending: options.pending || false,
        });
    };
    AsyncState.clone = function (asyncState) {
        return Utils.assign({}, asyncState);
    };
    AsyncState.reset = function (asyncState) {
        return AsyncState.create(asyncState.defaultValue);
    };
    AsyncState.resolve = function (asyncState, value, metaUpdate) {
        if (value === undefined) {
            throw new Error("[react-async-stateful] Cannot resolve async asyncState to 'undefined', did you mean 'reset(asyncState)'?");
        }
        return Utils.assign({}, asyncState, {
            error: undefined,
            cancelled: false,
            pending: false,
            rejected: false,
            resolved: true,
            resolvedAt: Date.now(),
            settled: true,
            settledAt: Date.now(),
            value: value,
            elementState: {},
            meta: resolveMetaUpdate(asyncState.meta, metaUpdate),
        });
    };
    AsyncState.reject = function (asyncState, error) {
        var _a;
        return Utils.assign({}, asyncState, {
            error: error,
            cancelled: false,
            pending: false,
            rejected: true,
            rejectedAt: Date.now(),
            settled: true,
            settledAt: Date.now(),
            resolved: false,
            value: (_a = asyncState.defaultValue) !== null && _a !== void 0 ? _a : undefined,
        });
    };
    AsyncState.cancel = function (asyncState) {
        var _a;
        return Utils.assign({}, asyncState, {
            error: null,
            cancelled: true,
            cancelledAt: Date.now(),
            pending: false,
            rejected: false,
            settled: false,
            resolved: false,
            value: (_a = asyncState.defaultValue) !== null && _a !== void 0 ? _a : undefined,
        });
    };
    AsyncState.submit = function (asyncState) {
        var _a;
        return Utils.assign({}, asyncState, {
            error: null,
            cancelled: false,
            pending: true,
            pendingAt: Date.now(),
            rejected: false,
            resolved: false,
            settled: false,
            value: (_a = asyncState.defaultValue) !== null && _a !== void 0 ? _a : undefined,
            submitType: "submit",
        });
    };
    AsyncState.refresh = function (asyncState) {
        return Utils.assign({}, asyncState, {
            cancelled: false,
            pending: true,
            pendingAt: Date.now(),
            settled: false,
            submitType: "refresh",
        });
    };
    /**
     * @description Alias for `refresh`
     */
    AsyncState.pending = function (asyncState) {
        return AsyncState.refresh(asyncState);
    };
    AsyncState.getStatus = function (asyncState) {
        if (AsyncState.isPristine(asyncState))
            return "pristine";
        if (asyncState.pending && asyncState.submitType === "refresh") {
            return "refreshing";
        }
        if (asyncState.pending && asyncState.submitType === "submit") {
            return "submitting";
        }
        if (asyncState.resolved) {
            return "resolved";
        }
        if (asyncState.rejected) {
            return "rejected";
        }
        return "invalid";
    };
    AsyncState.isRejected = function (asyncState) {
        return asyncState.rejected;
    };
    AsyncState.isPending = function (asyncState) {
        return asyncState.pending;
    };
    AsyncState.isResolved = function (asyncState) {
        return asyncState.resolved;
    };
    AsyncState.isCancelled = function (asyncState) {
        return asyncState.cancelled;
    };
    AsyncState.isPristine = function (asyncState) {
        return !AsyncState.isRejected(asyncState) && !AsyncState.isResolved(asyncState) && !AsyncState.isPending(asyncState);
    };
    AsyncState.isSettled = function (asyncState) {
        return asyncState.settled;
    };
    /**
     * @description Patch the value of a **resolved** asyncState.
     *
     * @throws {Error} if `asyncState` is not resolved
     */
    AsyncState.patch = function (asyncState, func) {
        if (AsyncState.isResolved(asyncState)) {
            return Utils.assign({}, asyncState, {
                value: func(asyncState.value),
            });
        }
        throw new Error("[react-async-stateful] Can only patch 'resolved' states, asyncState was " + AsyncState.getStatus(asyncState));
    };
    AsyncState.map = function (asyncState, mapFunc, defaultValue) {
        var mapped = this.clone(asyncState);
        if (this.isResolved(asyncState)) {
            var result = mapFunc(asyncState.value);
            Utils.assign(mapped, { value: result });
        }
        else {
            Utils.assign(mapped, { value: defaultValue });
        }
        return mapped;
    };
    AsyncState.match = function (asyncState, cases, defaultValue) {
        var status = AsyncState.getStatus(asyncState);
        if (status in cases) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            var value = cases[status];
            if (status === "resolved") {
                return typeof value === "function" ? value(asyncState.value) : value;
            }
            else if (status === "rejected") {
                return typeof value === "function" ? value(asyncState.error) : value;
            }
            return value;
        }
        return defaultValue;
    };
    AsyncState.updateMeta = function (asyncState, metaUpdate) {
        return Utils.assign({}, asyncState, {
            meta: resolveMetaUpdate(asyncState.meta, metaUpdate),
        });
    };
    return AsyncState;
}());
export { AsyncState };
export default AsyncState;
